/**
 * Grid Mapping Utilities
 * Maps body positions to the 5x5 exercise grid
 * 
 * Grid Coordinate System:
 * - Chambers (X): 1-5, left to right FROM USER'S PERSPECTIVE
 * - Levels (Y): 1-5, bottom to top
 * - Origin: (1,1) = bottom-left from user's view
 * 
 * IMPORTANT: Front camera coordinates are MIRRORED
 * - User's LEFT hand appears on RIGHT side of raw video frame
 * - We must mirror X coordinates to match user's perspective
 * 
 * The grid corresponds to the body diagram:
 * - Chamber 1 = User's Far Left (high X in raw camera)
 * - Chamber 5 = User's Far Right (low X in raw camera)
 * - Level 1 = Legs/Lower (high Y in camera)
 * - Level 5 = Head/Upper (low Y in camera)
 */

const GRID_SIZE = 5;

// Get stored calibration data
const getCalibration = () => {
  try {
    const stored = localStorage.getItem('movementCalibration');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/**
 * Map a normalized position (0-1) to grid coordinates (1-5)
 * Assumes coordinates are already in USER'S perspective (mirrored for front camera)
 * @param {number} normalizedX - X position from 0 (user's left) to 1 (user's right)
 * @param {number} normalizedY - Y position from 0 (top/head) to 1 (bottom/feet)
 * @returns {{chamber: number, level: number}} Grid position
 */
export const mapToGrid = (normalizedX, normalizedY) => {
  // X maps to chambers: 0 = Chamber 1 (left), 1 = Chamber 5 (right)
  const chamber = Math.min(GRID_SIZE, Math.max(1, Math.ceil(normalizedX * GRID_SIZE)));
  
  // Y is inverted: normalizedY 0 = top = level 5, normalizedY 1 = bottom = level 1
  const level = Math.min(GRID_SIZE, Math.max(1, Math.ceil((1 - normalizedY) * GRID_SIZE)));
  
  return { chamber, level };
};

/**
 * Map position using calibration data for accurate grid mapping
 * Calibration stores USER-PERSPECTIVE coordinates (already mirrored)
 * @param {number} x - X position in pixels (user's perspective, mirrored)
 * @param {number} y - Y position in pixels  
 * @param {object} calibration - Calibration bounds {minX, maxX, minY, maxY}
 * @returns {{chamber: number, level: number}} Grid position
 */
export const mapWithCalibration = (x, y, calibration) => {
  if (!calibration || !calibration.isCalibrated) {
    return null;
  }

  const { minX, maxX, minY, maxY } = calibration;
  
  // Normalize position within calibrated bounds
  // minX = user's left edge, maxX = user's right edge
  const normalizedX = Math.max(0, Math.min(1, (x - minX) / (maxX - minX)));
  const normalizedY = Math.max(0, Math.min(1, (y - minY) / (maxY - minY)));
  
  return mapToGrid(normalizedX, normalizedY);
};

/**
 * Map body center position to grid
 * @param {{x: number, y: number}} bodyCenter - Body center in RAW pixel coordinates
 * @param {number} frameWidth - Video frame width
 * @param {number} frameHeight - Video frame height
 * @param {object} calibration - Optional calibration data (stores mirrored coords)
 * @returns {{chamber: number, level: number} | null} Grid position or null
 */
export const mapBodyToGrid = (bodyCenter, frameWidth, frameHeight, calibration = null) => {
  if (!bodyCenter) return null;
  
  // Mirror X for front camera: convert raw coords to user's perspective
  const mirroredX = frameWidth - bodyCenter.x;
  
  // Try calibrated mapping first
  const cal = calibration || getCalibration();
  if (cal && cal.isCalibrated) {
    return mapWithCalibration(mirroredX, bodyCenter.y, cal);
  }
  
  // Fall back to frame-based mapping (using mirrored X)
  const normalizedX = mirroredX / frameWidth;
  const normalizedY = bodyCenter.y / frameHeight;
  
  return mapToGrid(normalizedX, normalizedY);
};

/**
 * Map hand position to grid
 * @param {{x: number, y: number}} handPosition - Hand position in RAW pixel coordinates
 * @param {number} frameWidth - Video frame width
 * @param {number} frameHeight - Video frame height
 * @param {object} calibration - Optional calibration data (stores mirrored coords)
 * @returns {{chamber: number, level: number} | null} Grid position or null
 */
export const mapHandToGrid = (handPosition, frameWidth, frameHeight, calibration = null) => {
  if (!handPosition) return null;
  
  // Mirror X for front camera: convert raw coords to user's perspective
  const mirroredX = frameWidth - handPosition.x;
  
  // Try calibrated mapping first
  const cal = calibration || getCalibration();
  if (cal && cal.isCalibrated) {
    return mapWithCalibration(mirroredX, handPosition.y, cal);
  }
  
  // Fall back to frame-based mapping (using mirrored X)
  const normalizedX = mirroredX / frameWidth;
  const normalizedY = handPosition.y / frameHeight;
  
  return mapToGrid(normalizedX, normalizedY);
};

/**
 * Check if calibration exists and is valid
 * @returns {boolean}
 */
export const hasCalibration = () => {
  const cal = getCalibration();
  return cal && cal.isCalibrated;
};

/**
 * Clear stored calibration
 */
export const clearCalibration = () => {
  localStorage.removeItem('movementCalibration');
};

/**
 * Calculate accuracy score for a ride pattern
 * @param {Array<{chamber: number, level: number}>} movementHistory - Array of grid positions
 * @param {string} rideType - The ride type pattern ('chamber-ride', 'level-ride', 'chamber-lock', 'level-lock', 'x-factor')
 * @param {number} expectedChamber - Expected chamber for lock patterns
 * @param {number} expectedLevel - Expected level for lock patterns
 * @returns {{accuracy: number, details: object}} Accuracy percentage and details
 */
export const calculatePatternAccuracy = (movementHistory, rideType, expectedChamber, expectedLevel) => {
  if (!movementHistory || movementHistory.length === 0) {
    return { accuracy: 0, details: { message: 'No movement recorded' } };
  }
  
  const uniquePositions = new Set(movementHistory.map(p => `${p.chamber}-${p.level}`));
  const chambersVisited = new Set(movementHistory.map(p => p.chamber));
  const levelsVisited = new Set(movementHistory.map(p => p.level));
  
  let accuracy = 0;
  let details = {};
  
  switch (rideType) {
    case 'chamber-ride':
      // Should move through all 5 chambers while staying at similar levels
      accuracy = (chambersVisited.size / GRID_SIZE) * 100;
      details = {
        chambersVisited: Array.from(chambersVisited).sort((a, b) => a - b),
        expectedChambers: GRID_SIZE,
        message: `Visited ${chambersVisited.size}/${GRID_SIZE} chambers`,
      };
      break;
      
    case 'level-ride':
      // Should move through all 5 levels while staying at similar chambers
      accuracy = (levelsVisited.size / GRID_SIZE) * 100;
      details = {
        levelsVisited: Array.from(levelsVisited).sort((a, b) => a - b),
        expectedLevels: GRID_SIZE,
        message: `Visited ${levelsVisited.size}/${GRID_SIZE} levels`,
      };
      break;
      
    case 'chamber-lock':
      // Should stay in the expected chamber while moving through levels
      const chamberLockAccuracy = movementHistory.filter(p => p.chamber === expectedChamber).length / movementHistory.length;
      const levelsCoveredInLock = new Set(movementHistory.filter(p => p.chamber === expectedChamber).map(p => p.level));
      accuracy = chamberLockAccuracy * 50 + (levelsCoveredInLock.size / GRID_SIZE) * 50;
      details = {
        expectedChamber,
        timeInChamber: `${Math.round(chamberLockAccuracy * 100)}%`,
        levelsVisited: Array.from(levelsCoveredInLock).sort((a, b) => a - b),
        message: `Stayed in chamber ${expectedChamber} ${Math.round(chamberLockAccuracy * 100)}% of time`,
      };
      break;
      
    case 'level-lock':
      // Should stay at the expected level while moving through chambers
      const levelLockAccuracy = movementHistory.filter(p => p.level === expectedLevel).length / movementHistory.length;
      const chambersCoveredInLock = new Set(movementHistory.filter(p => p.level === expectedLevel).map(p => p.chamber));
      accuracy = levelLockAccuracy * 50 + (chambersCoveredInLock.size / GRID_SIZE) * 50;
      details = {
        expectedLevel,
        timeAtLevel: `${Math.round(levelLockAccuracy * 100)}%`,
        chambersVisited: Array.from(chambersCoveredInLock).sort((a, b) => a - b),
        message: `Stayed at level ${expectedLevel} ${Math.round(levelLockAccuracy * 100)}% of time`,
      };
      break;
      
    case 'x-factor':
      // Should move diagonally - visiting multiple chambers AND levels
      const diagonalScore = Math.min(chambersVisited.size, levelsVisited.size);
      accuracy = (diagonalScore / GRID_SIZE) * 100;
      details = {
        chambersVisited: Array.from(chambersVisited).sort((a, b) => a - b),
        levelsVisited: Array.from(levelsVisited).sort((a, b) => a - b),
        uniquePositions: uniquePositions.size,
        message: `Diagonal coverage: ${diagonalScore}/${GRID_SIZE}`,
      };
      break;
      
    default:
      accuracy = 0;
      details = { message: 'Unknown pattern type' };
  }
  
  return { accuracy: Math.round(accuracy), details };
};

/**
 * Generate a heatmap of position frequencies
 * @param {Array<{chamber: number, level: number}>} movementHistory - Array of grid positions
 * @returns {Array<Array<number>>} 5x5 array of frequencies (0-1 normalized)
 */
export const generateHeatmap = (movementHistory) => {
  // Initialize 5x5 grid with zeros
  const heatmap = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(0));
  
  if (!movementHistory || movementHistory.length === 0) {
    return heatmap;
  }
  
  // Count occurrences
  movementHistory.forEach(pos => {
    if (pos.chamber >= 1 && pos.chamber <= 5 && pos.level >= 1 && pos.level <= 5) {
      // Array indices: level 5 = row 0, level 1 = row 4
      const rowIndex = GRID_SIZE - pos.level;
      const colIndex = pos.chamber - 1;
      heatmap[rowIndex][colIndex]++;
    }
  });
  
  // Normalize to 0-1 range
  const maxCount = Math.max(...heatmap.flat(), 1);
  return heatmap.map(row => row.map(count => count / maxCount));
};

/**
 * Analyze movement patterns for assessment integration
 * @param {Array<{chamber: number, level: number}>} movementHistory - Array of grid positions
 * @returns {object} Analysis results
 */
export const analyzeMovementPatterns = (movementHistory) => {
  if (!movementHistory || movementHistory.length === 0) {
    return {
      mostVisitedChamber: null,
      leastVisitedChamber: null,
      mostVisitedLevel: null,
      leastVisitedLevel: null,
      dominantSide: null,
      verticalBias: null,
      suggestions: [],
    };
  }
  
  // Count chamber visits
  const chamberCounts = {};
  const levelCounts = {};
  
  for (let i = 1; i <= GRID_SIZE; i++) {
    chamberCounts[i] = 0;
    levelCounts[i] = 0;
  }
  
  movementHistory.forEach(pos => {
    chamberCounts[pos.chamber]++;
    levelCounts[pos.level]++;
  });
  
  // Find most and least visited
  const sortedChambers = Object.entries(chamberCounts).sort((a, b) => b[1] - a[1]);
  const sortedLevels = Object.entries(levelCounts).sort((a, b) => b[1] - a[1]);
  
  const mostVisitedChamber = parseInt(sortedChambers[0][0]);
  const leastVisitedChamber = parseInt(sortedChambers[sortedChambers.length - 1][0]);
  const mostVisitedLevel = parseInt(sortedLevels[0][0]);
  const leastVisitedLevel = parseInt(sortedLevels[sortedLevels.length - 1][0]);
  
  // Determine dominant side (left chambers 1-2, center 3, right 4-5)
  const leftCount = chamberCounts[1] + chamberCounts[2];
  const rightCount = chamberCounts[4] + chamberCounts[5];
  const centerCount = chamberCounts[3];
  
  let dominantSide = 'balanced';
  if (leftCount > rightCount * 1.3) dominantSide = 'left';
  else if (rightCount > leftCount * 1.3) dominantSide = 'right';
  
  // Determine vertical bias (low levels 1-2, mid 3, high 4-5)
  const lowCount = levelCounts[1] + levelCounts[2];
  const highCount = levelCounts[4] + levelCounts[5];
  
  let verticalBias = 'balanced';
  if (lowCount > highCount * 1.3) verticalBias = 'low';
  else if (highCount > lowCount * 1.3) verticalBias = 'high';
  
  // Generate suggestions
  const suggestions = [];
  
  if (dominantSide === 'left') {
    suggestions.push('Practice movements towards chambers 4 and 5 (right side)');
  } else if (dominantSide === 'right') {
    suggestions.push('Practice movements towards chambers 1 and 2 (left side)');
  }
  
  if (verticalBias === 'low') {
    suggestions.push('Challenge yourself with higher level movements (levels 4-5)');
  } else if (verticalBias === 'high') {
    suggestions.push('Incorporate more grounded movements (levels 1-2)');
  }
  
  if (leastVisitedChamber) {
    suggestions.push(`Chamber ${leastVisitedChamber} needs more attention`);
  }
  
  if (leastVisitedLevel) {
    suggestions.push(`Level ${leastVisitedLevel} is underutilized in your practice`);
  }
  
  return {
    mostVisitedChamber,
    leastVisitedChamber,
    mostVisitedLevel,
    leastVisitedLevel,
    dominantSide,
    verticalBias,
    chamberCounts,
    levelCounts,
    suggestions,
  };
};
